/**
 * RTK Query Base Query for Django REST Framework API
 * Custom base query for Django backend communication
 *
 * Authentication:
 * - JWT tokens (access_token, refresh_token) are sent as secure httpOnly cookies
 * - Credentials: 'include' ensures cookies are sent with requests
 * - Authorization header not needed (tokens in cookies)
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
// R4: resetApiState.js imports all 19 RTK Query API slices (riskApi,
// controlApi, ... down to platformOpsApi), every one of which is itself
// built with `createApi({ baseQuery: baseQueryWithReauth, ... })` from
// *this* file. A static top-level import here would create a real import
// cycle (this file -> resetApiState.js -> every entity api/*.js file ->
// this file again) -- risky in production (a cycle can leave a binding in
// its temporal-dead-zone depending on which module happens to load
// first) and, concretely, it broke tests that partially mock any single
// one of those 19 modules (e.g. `vi.mock('entities/identity/api/adminApi',
// () => ({ useGetUsersQuery: ... }))` with no `identityAdminApi` export)
// the moment that test's component tree imported baseQuery.js at all --
// which is nearly every component, since baseQuery.js is the base query
// for every entity API. A dynamic import here defers resolution until the
// (rare, runtime-only) forced-logout branches actually run, breaking the
// static cycle entirely while leaving behavior unchanged.

/**
 * Base URL for Django API
 */
const DJANGO_API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

let refreshPromise = null;

const getStoredRefreshToken = () => {
  try {
    return localStorage.getItem('refresh_token');
  } catch {
    return null;
  }
};

/**
 * Fetch-based base query for Django REST Framework
 * Handles authentication, tenant context, and error responses
 */
export const baseQuery = fetchBaseQuery({
  baseUrl: DJANGO_API_BASE_URL,
  credentials: 'include', // Include httpOnly cookies with requests
  prepareHeaders: (headers, { getState }) => {
    // Content-Type is intentionally NOT set here.
    // The browser sets it automatically:
    //   - multipart/form-data (with boundary) when the body is FormData
    //   - application/json for plain-object bodies via fetchBaseQuery defaults
    // Manually setting Content-Type: application/json breaks file upload endpoints.

    // Add tenant context header for multi-tenancy
    // Read from Redux store (state is available here too) or sessionStorage
    let tenantId = sessionStorage.getItem('tenant_id');
    const INVALID_VALUES = new Set(['', 'null', 'undefined', 'None']);

    if (!tenantId || INVALID_VALUES.has(tenantId)) {
      // Try to get from Redux state if available
      const state = getState();
      tenantId = state?.auth?.tenantId || null;
    }

    if (tenantId && !INVALID_VALUES.has(tenantId)) {
      headers.set('X-Tenant-ID', tenantId);
    }

    // Add Authorization header when access token is available.
    // Some DRF endpoints still rely on Bearer auth even with cookie sessions.
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Add correlation ID for request tracing
    const correlationId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    headers.set('X-Correlation-ID', correlationId);

    return headers;
  },
  responseHandler: async (response) => {
    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return null;
    }

    // Handle JSON responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // Handle text responses
    return response.text();
  },
});

/**
 * Custom base query with automatic token refresh
 * Wraps the base query to handle 401 responses and refresh tokens
 */
export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  const requestUrl = typeof args === 'string' ? args : args?.url;
  const isRefreshRequest = requestUrl === '/api/v1/auth/token/refresh/';

  // SHR-18 (review round 2): a 401 whose body is specifically
  // {'detail': 'Session expired.'} comes from TenantContextMiddleware's
  // admin-configured session_timeout enforcement
  // (app/django-core/apps/shared/middleware.py::_session_timed_out), not
  // from an ordinary expired/invalid access token. The refresh endpoint
  // mints a brand-new access token with a fresh `iat` on every call, so
  // silently refreshing+retrying here would defeat session_timeout
  // entirely -- the "session" would just reset on every 401 for as long as
  // the underlying (longer-lived) refresh token/cookie remains valid.
  // Treat this specific 401 as terminal: clear local tokens/tenant context
  // and let it surface so the app forces re-login, instead of attempting
  // the normal silent-refresh-and-retry flow below.
  const isSessionTimeout =
    result.error && result.error.status === 401 && result.error.data?.detail === 'Session expired.';

  if (isSessionTimeout) {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch {
      /* localStorage unavailable */
    }
    try {
      sessionStorage.removeItem('tenant_id');
    } catch {
      /* sessionStorage unavailable */
    }
    // R4: this is a forced logout -- clear every RTK Query cache slice so
    // a subsequent login never sees this session's stale cached data.
    const { resetAllApiState } = await import('./resetApiState');
    resetAllApiState(api.dispatch);
    return result;
  }

  // We got a 401: always attempt a refresh, even with no stored refresh token —
  // cookie-login sessions carry the refresh token as an httpOnly cookie
  // (credentials: 'include'), so the refresh endpoint still succeeds from the
  // cookie alone (SHR-01). Single-flight across concurrent 401s (module-level
  // refreshPromise) so simultaneous requests share one refresh call.
  if (result.error && result.error.status === 401 && !isRefreshRequest) {
    if (!refreshPromise) {
      const refreshToken = getStoredRefreshToken();
      const body = refreshToken ? { refresh: refreshToken } : {};

      refreshPromise = baseQuery(
        {
          url: '/api/v1/auth/token/refresh/',
          method: 'POST',
          body,
        },
        api,
        extraOptions
      ).finally(() => {
        refreshPromise = null;
      });
    }

    const refreshResult = await refreshPromise;

    if (refreshResult?.data) {
      const newAccessToken = refreshResult.data?.access;
      const newRefreshToken = refreshResult.data?.refresh;

      if (newAccessToken) {
        try {
          localStorage.setItem('access_token', newAccessToken);
        } catch {
          /* localStorage unavailable (private mode etc.) */
        }
      }
      if (newRefreshToken) {
        try {
          localStorage.setItem('refresh_token', newRefreshToken);
        } catch {
          /* localStorage unavailable */
        }
      }

      // Retry the original request now that we have a fresh access token.
      result = await baseQuery(args, api, extraOptions);
    } else {
      const refreshStatus = refreshResult?.error?.status;

      // Log out ONLY when the refresh endpoint itself rejects the session
      // (401/403 = refresh token invalid, expired or revoked). A network
      // error, timeout or 5xx from the refresh call means we could not tell
      // whether the session is still valid — SHR-02: leave tokens and tenant
      // context intact and let the original 401 surface, rather than forcing
      // every transient outage into a logout.
      if (refreshStatus === 401 || refreshStatus === 403) {
        try {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        } catch {
          /* localStorage unavailable */
        }
        try {
          sessionStorage.removeItem('tenant_id');
        } catch {
          /* sessionStorage unavailable */
        }
        // R4: refresh was rejected -- this is a forced logout too, so
        // clear every RTK Query cache slice.
        const { resetAllApiState } = await import('./resetApiState');
        resetAllApiState(api.dispatch);
      }
    }
  }

  return result;
};

/**
 * Parse Django REST Framework error response
 * @param {Object} error - Error object from baseQuery
 * @returns {Object} Parsed error with user-friendly message
 */
export const parseDjangoError = (error) => {
  if (!error) {
    return { message: 'An unknown error occurred' };
  }

  // Handle validation errors
  if (error.status === 400 && error.data) {
    const validationErrors = [];

    if (typeof error.data === 'object') {
      Object.entries(error.data).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          validationErrors.push(`${field}: ${messages.join(', ')}`);
        } else if (typeof messages === 'string') {
          validationErrors.push(`${field}: ${messages}`);
        }
      });
    }

    return {
      message: validationErrors.length > 0 ? validationErrors.join('; ') : 'Validation error',
      details: error.data,
    };
  }

  // Handle common HTTP errors
  const errorMessages = {
    401: 'Authentication required. Please log in.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'Conflict: The resource already exists or is in an invalid state.',
    422: 'Invalid data provided.',
    500: 'Server error. Please try again later.',
    502: 'Service temporarily unavailable.',
    503: 'Service temporarily unavailable.',
  };

  return {
    message:
      error.data?.detail ||
      error.data?.message ||
      errorMessages[error.status] ||
      'An error occurred',
    status: error.status,
  };
};

export default baseQuery;
